import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { CountryCode, Entity } from '@debbie/common/dist/index.js'
import { CaseStatus } from '@debbie/common/dist/cases/index.js'
import { getContactPerson } from '@debbie/common/dist/customers/index.js'
import { RefIdTypes } from '@debbie/common/dist/documents/index.js'
import { EmbedPlacement, getEmbedAreas } from '@debbie/common/dist/embeds/index.js'
import { isUUID } from '@debbie/common/dist/imports/data/helpers.js'
import { TodoType } from '@debbie/common/dist/workflows/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { selectCaseUpdates } from '@debbie/cortex/dist/case/selectors.js'
import { selectCaseById } from '@debbie/cortex/dist/cases/selectors.js'
import { useConnectMappingEntityMappings } from '@debbie/cortex/dist/connect//index.js'
import { selectCustomerById } from '@debbie/cortex/dist/customers/selectors.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { selectTenant } from '@debbie/cortex/dist/tenant/selectors.js'
import {
  Panel,
  CaseInfo,
  CasePauseStatus,
  CopyToClipboard,
  SubscribedToCosmo,
  Button,
  Modal,
  Properties,
  EmbedsFrame,
  ResponsiblePreview,
} from '@debbie/iris/dist/components/index.js'
import CreditorNotes from '@debbie/iris/dist/components/CreditorInfo/CreditorNotes.js'
import Documents from '@debbie/iris/dist/components/documents/Documents.js'
import { Row, Col } from '@debbie/iris/dist/components/Grid/index.js'
import CaseHistory from '@debbie/iris/dist/components/Hist/CaseHistory.js'
import CreateTribunalCase from '@debbie/iris/dist/components/tribunal/CreateTribunalCase/index.js'
import Tribunal from '@debbie/iris/dist/components/tribunal/Tribunal.js'

import NewInteraction from '../../components/NewInteraction.js'

import { CaseEconomy, CaseHeader } from './index.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { useVariation } from '@debbie/iris/dist/feature-flags/index.js'
import { FeatureFlag } from '@debbie/common/dist/feature-flags/index.js'

type Props = {
  id: string
  outboundMode?: boolean
}

const CaseSummary = (props: Props) => {
  const tenant = useSelector(selectTenant)

  const [showNewUpdate, setShowNewUpdate] = useState(false)
  const [tribunalModalOpen, setTribunalModalOpen] = useState(false)

  const lang = useSelector((s: RootState) => s.lang)
  const singleCase = useSelector((s: RootState) =>
    selectCaseById(s, isUUID(props.id) ? props.id : Number(props.id)),
  )
  const customer = useSelector(
    (s: RootState) =>
      singleCase && selectCustomerById(s, singleCase.customerId),
  )
  const caseInteractionsLoadable = useInteractionsByReference(
    Entity.Case,
    singleCase?.caseId,
    true,
  )
  const caseInteractions = h.hasData(caseInteractionsLoadable)
    ? caseInteractionsLoadable.data
    : null

  const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId)
  const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null

  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const updates = useSelector((s: RootState) =>
    singleCase ? selectCaseUpdates(s, singleCase.caseId) : null,
  )

  const usingConnect = !!updates?.find((u) => {
    return u.procedure?.find((p) => p.type === TodoType.ConnectToDebtCollector)
  })

  const entityMappings = useConnectMappingEntityMappings(singleCase?.caseId, {
    enabled: usingConnect,
  })
  const connectMappings = h.hasData(entityMappings) ? entityMappings.data : {}

  const embedAreas = tenant
    ? getEmbedAreas(tenant.embeds, EmbedPlacement.CaseworkerWebCasePage)
    : []

  const contactPerson = customer && getContactPerson(customer)

  const hasTribunal = useVariation(FeatureFlag.TRIBUNAL)

  return (
    <Row className="ui-test-c9bb9cb0">
      {singleCase && customer && (
        <CaseHeader
          case={singleCase}
          customer={customer}
          id={`${singleCase?.id}`}
          setShowNewUpdate={setShowNewUpdate}
          showNewUpdate={showNewUpdate}
        />
      )}
      <Col size="md" width="12">
        {singleCase && (
          <CasePauseStatus
            case={singleCase}
            setShowNewUpdate={setShowNewUpdate}
            showNewUpdate={showNewUpdate}
          />
        )}
      </Col>
      <Col size="md" width="5">
        <Panel
          header={
            <>
              <CopyToClipboard text={singleCase?.caseId} hideIcon>
                {lang.cases.caseInfo}
              </CopyToClipboard>
              <small style={{ float: 'right', marginTop: '-7px' }}>
                {singleCase &&
                  [CaseStatus.PENDING, CaseStatus.VERIFIED].includes(
                    singleCase.status,
                  ) && <SubscribedToCosmo />}
              </small>
            </>
          }
          bodyStyle={{
            minHeight: '350px',
          }}
        >
          <CaseInfo caseId={props.id} />
          <br />
          {creditor && <CreditorNotes creditor={creditor} />}
          {singleCase &&
            singleCase.responsibleReferenceId &&
            singleCase.responsibleType && (
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <p style={{ marginRight: '5px' }}>{lang.cases.responsible}:</p>
                <ResponsiblePreview
                  responsibleReferenceId={singleCase.responsibleReferenceId}
                  responsibleType={singleCase.responsibleType}
                />
              </div>
            )}
        </Panel>

        <CaseEconomy case={singleCase} id={props.id} />
        {embedAreas &&
          singleCase &&
          embedAreas.map((embedArea) => {
            if (embedArea.placement !== EmbedPlacement.CaseworkerWebCasePage)
              return null

            return (
              <Panel
                header={embedArea.title}
                bodyStyle={{ overflow: 'hidden' }}
                key={embedArea.embedId}
              >
                <div
                  style={{
                    margin: '-20px -20px -25px -20px',
                  }}
                >
                  <EmbedsFrame
                    id={embedArea.embedId}
                    height={embedArea.details.height}
                    context={{
                      caseId: singleCase.caseId,
                    }}
                  />
                </div>
              </Panel>
            )
          })}
        {singleCase && caseInteractions && (
          <Documents
            case={singleCase}
            name={contactPerson?.details?.name}
            role={
              contactPerson?.relation === 'alternative-contact'
                ? lang.debtors.alternativeContact
                : lang.debtors.debtor
            }
            refId={singleCase.caseId}
            refIdType={RefIdTypes.CASE}
            connectMappings={connectMappings}
          />
        )}
        {tribunalModalOpen && singleCase && (
          <Modal
            onToggle={() => setTribunalModalOpen(!tribunalModalOpen)}
            header="Domstolsmodul"
          >
            <CreateTribunalCase
              caseSeqId={singleCase.id}
              tenantId={singleCase.tenantId}
              onCreated={() => setTribunalModalOpen(false)}
            />
          </Modal>
        )}
        {hasTribunal &&
          singleCase &&
          caseVouchers &&
          tenant &&
          ([CountryCode.DK, CountryCode.GL] as string[]).includes(
            tenant.countryCode,
          ) && (
            <Panel
              header={
                <span>
                  Domstolsmodul{' '}
                  <Button
                    style={{ float: 'right' }}
                    onClick={() => setTribunalModalOpen(!tribunalModalOpen)}
                    size="mini"
                    theme="secondary"
                    className="ui-test-2cc0b2a5"
                  >
                    {lang.tribunal.addTribunalCase}
                  </Button>
                </span>
              }
            >
              <Tribunal
                case={singleCase}
                tenantId={singleCase.tenantId}
                caseVouchers={caseVouchers}
                creditorId={singleCase.creditorId}
              />
            </Panel>
          )}

        {singleCase && (
          <Panel header={lang.properties.properties}>
            <Properties
              propRef={Entity.Case}
              propRefId={singleCase.caseId}
              {...(singleCase.caseGroup?.id
                ? {
                    scope: 'case_groups',
                    scopeId: singleCase.caseGroup.id,
                  }
                : {
                    scope: null,
                    scopeId: null,
                  })}
            />
          </Panel>
        )}
      </Col>
      <Col size="md" width="7">
        <Row>
          <Col size="md" width="12">
            {singleCase && (
              <NewInteraction
                referenceId={singleCase.caseId}
                referenceType={Entity.Case}
                key={`${singleCase.caseId}-${contactPerson?.userId}`}
                outboundMode={props.outboundMode}
              />
            )}
          </Col>
          <Col size="md" width="12">
            <br />
            {singleCase && <CaseHistory caseId={props.id} />}
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default CaseSummary
